import React, { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BDK, waUrl, SITE_URL, NEXO_ENABLED } from "../config/bdk";
import useReveal from "../hooks/useReveal";
import usePricingData from "../hooks/usePricingData";
import { formatPriceMXN } from "../lib/pricingFormat";
import Seo from "../components/Seo";
import { Footer } from "../components/landing/FinalCTAFooter";
import WhatsAppFloat from "../components/landing/WhatsAppFloat";
import WhatsAppLeadModal from "../components/landing/WhatsAppLeadModal";

const buildPricingJsonLd = (categories) => {
  const allItems = categories.flatMap((c) => c.items);
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
        {
          "@type": "ListItem",
          position: 2,
          name: "Lista de precios completa",
          item: `${SITE_URL}/tipos-de-paginas-web`,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: allItems.map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: item.name,
          description: item.description,
          provider: { "@type": "Organization", name: BDK.companyLegalName },
          ...(item.price != null && {
            offers: {
              "@type": "Offer",
              price: String(item.price),
              priceCurrency: "MXN",
            },
          }),
        },
      })),
    },
  ];
};

// Collapsing/expanding an accordion above the fold shifts everything below
// it, so a plain state toggle leaves the viewport showing different content
// than what the visitor clicked on. Re-measure the clicked button before and
// after the (synchronous, via flushSync) DOM update and offset the scroll by
// the difference so the button stays put under the cursor.
const toggleWithScrollAnchor = (buttonEl, updateState) => {
  const prevTop = buttonEl.getBoundingClientRect().top;
  flushSync(updateState);
  const newTop = buttonEl.getBoundingClientRect().top;
  window.scrollBy(0, newTop - prevTop);
};

const MiniHeader = () => (
  <header
    data-testid="page-types-header"
    className="relative"
    style={{ background: "var(--ink)", borderBottom: "1px solid #2a2b34" }}
  >
    <div className="container-bdk flex items-center justify-between gap-4 h-[72px]">
      <Link
        to="/"
        data-testid="page-types-logo"
        className="block w-[170px] md:w-[190px] flex-shrink-0"
      >
        <img
          src={BDK.logos.redWhite}
          alt="BDKSISTEMAS"
          className="w-full h-auto object-contain"
        />
      </Link>
      <Link
        to="/#tipos-paginas"
        data-testid="page-types-back-home"
        className="inline-flex items-center gap-2 text-white/80 hover:text-[#d90020] text-[12px] sm:text-[13px] font-bold uppercase tracking-wide transition-colors"
      >
        <ArrowLeft size={16} />
        Volver al inicio
      </Link>
    </div>
  </header>
);

const ItemCard = ({ item, categoryId }) => {
  const priceDisplay = formatPriceMXN(item.price);
  const ctaMsg =
    item.price != null
      ? `Hola, quiero cotizar "${item.name}" (desde $${priceDisplay} MXN + IVA) con BDKSISTEMAS.`
      : `Hola, tengo un proyecto de "${item.name}". Quiero cotizarlo con BDKSISTEMAS.`;

  return (
    <article
      data-testid={`page-type-card-${item.code}`}
      className="flex flex-col p-6 rounded-[14px]"
      style={{ border: "1px solid var(--line)", background: "#fff" }}
    >
      <p className="font-display text-[18px] font-black uppercase leading-tight">
        {item.name}
      </p>
      <div
        className="font-display font-black leading-none my-3"
        style={{ color: "var(--red)", fontSize: "26px" }}
      >
        {item.price != null ? (
          <>
            <span className="text-[11px] font-bold uppercase tracking-wide align-top mr-1 text-[#666873]">
              Desde
            </span>
            <br />
            <span className="text-[15px] align-top mr-1">$</span>
            {priceDisplay}
            <small className="text-[11px] ml-1 font-black">MXN</small>
          </>
        ) : (
          <span className="text-[18px]">{item.quote_label}</span>
        )}
      </div>
      <p className="text-[13px] text-[#666873]">{item.description}</p>
      <ul className="grid gap-1.5 my-4 list-none">
        {item.includes.map((f, j) => (
          <li key={j} className="relative pl-4 text-[12.5px] text-[#333]">
            <span
              className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full"
              style={{ background: "var(--red)" }}
            />
            {f}
          </li>
        ))}
      </ul>
      <p className="text-[13px] mb-1 text-[#666873]">
        Tiempo estimado: <strong>{item.estimated_delivery}</strong>
      </p>
      {item.restrictions && (
        <p className="text-[13px] mb-4 text-[#666873]">{item.restrictions}</p>
      )}
      <a
        href={waUrl(ctaMsg)}
        target="_blank"
        rel="noreferrer"
        data-testid={`page-type-cta-${item.code}`}
        onClick={() =>
          window.fbq?.("trackCustom", "ClickTipoPagina", {
            categoria: categoryId,
            tipo: item.code,
          })
        }
        className="mt-auto bdk-btn bdk-btn--outline"
      >
        Cotizar este tipo
      </a>
    </article>
  );
};

const CategoryAccordion = ({ category, isOpen, onToggle }) => (
  <article
    data-testid={`category-${category.id}`}
    style={{ borderBottom: "1px solid var(--line)" }}
  >
    <button
      type="button"
      onClick={(e) => onToggle(e.currentTarget)}
      aria-expanded={isOpen}
      data-testid={`category-toggle-${category.id}`}
      className="w-full flex justify-between items-center gap-5 py-6 text-left cursor-pointer"
    >
      <span>
        <span className="block font-display text-[20px] md:text-[24px] font-black uppercase leading-tight">
          {category.name}
        </span>
        <span className="block text-[13px] text-[#666873] mt-1">
          {category.subtitle}
        </span>
      </span>
      <span
        className="font-display text-[28px] font-black transition-transform flex-shrink-0"
        style={{
          color: "var(--red)",
          transform: isOpen ? "rotate(45deg)" : "rotate(0)",
        }}
      >
        +
      </span>
    </button>
    {isOpen && (
      <div className="grid gap-5 md:grid-cols-2 pb-8">
        {category.items.map((item) => (
          <ItemCard key={item.code} item={item} categoryId={category.id} />
        ))}
      </div>
    )}
  </article>
);

const PriceTable = ({ testId, rows }) => (
  <div
    data-testid={testId}
    className="rounded-[14px] overflow-hidden"
    style={{ border: "1px solid var(--line)" }}
  >
    {rows.map((r, i) => (
      <div
        key={i}
        className="flex items-center justify-between gap-4 px-5 py-3.5 text-[13px]"
        style={{
          background: i % 2 === 0 ? "#fff" : "var(--paper)",
          borderTop: i === 0 ? "none" : "1px solid var(--line)",
        }}
      >
        <span className="text-[#333]">{r.name}</span>
        <span className="font-bold flex-shrink-0" style={{ color: "var(--red)" }}>
          {r.price}
        </span>
      </div>
    ))}
  </div>
);

const PageTypesPricing = () => {
  useReveal();
  const { data } = usePricingData();
  const [openCategories, setOpenCategories] = useState(() => new Set([data.categories[0].id]));
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [waHref, setWaHref] = useState(null);
  const pricingJsonLd = buildPricingJsonLd(data.categories);

  useEffect(() => {
    const onClick = (e) => {
      const link = e.target.closest('a[href*="wa.me"]');
      if (link && !link.hasAttribute("data-no-lead-modal")) {
        e.preventDefault();
        setWaHref(link.href);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  return (
    <div
      data-testid="page-types-pricing-page"
      className="min-h-screen bg-white text-[#101010]"
    >
      <Seo
        title="Lista Completa de Precios: Tipos de Página Web | BDKSISTEMAS"
        description="Lista completa de precios base para diseño y desarrollo de sitios web: landing pages, sitios empresariales, menús digitales, catálogos, agendas, tiendas en línea y desarrollos especiales."
        path="/tipos-de-paginas-web"
        jsonLd={pricingJsonLd}
      />
      <MiniHeader />
      <main>
        <section
          data-testid="section-page-types-hero"
          className="bdk-section"
          style={{ background: "var(--paper)" }}
        >
          <div className="container-bdk max-w-[820px] mx-auto text-center">
            <p className="kicker">Lista de precios completa</p>
            <h1 className="font-display text-[38px] sm:text-[48px] md:text-[58px]">
              Todos los tipos de página web
              <br />
              que podemos hacer para ti.
            </h1>
            <p className="mt-5 text-[#666873] text-[15px] md:text-[16px]">
              17 tipos de sitio web con precio base, alcance e incluye claro.
              Precios en pesos mexicanos, más IVA. Elige la opción que más se
              acerca a tu negocio y revisamos juntos los detalles antes de
              cotizar formalmente.
            </p>
          </div>
        </section>

        <section
          data-testid="section-page-types-catalog"
          className="bdk-section"
        >
          <div className="container-bdk max-w-[980px] mx-auto">
            {data.categories.map((category) => (
              <CategoryAccordion
                key={category.id}
                category={category}
                isOpen={openCategories.has(category.id)}
                onToggle={(btnEl) =>
                  toggleWithScrollAnchor(btnEl, () =>
                    setOpenCategories((cur) => {
                      const next = new Set(cur);
                      if (next.has(category.id)) next.delete(category.id);
                      else next.add(category.id);
                      return next;
                    })
                  )
                }
              />
            ))}
          </div>
        </section>

        <section
          data-testid="section-page-types-addons"
          className="bdk-section bdk-split-bg"
        >
          <div className="container-bdk max-w-[980px] mx-auto grid gap-10 md:grid-cols-2">
            <div>
              <p className="kicker">Ampliaciones</p>
              <h2 className="font-display text-[26px] md:text-[30px] mb-5">
                Servicios y módulos adicionales
              </h2>
              <p className="text-[13px] text-[#666873] mb-5">
                Precios de referencia para cuando un proyecto estándar
                necesita ampliar su alcance. La cotización formal indicará
                cuáles aplican.
              </p>
              <PriceTable
                testId="table-addon-modules"
                rows={data.addonModules.map((a) => ({ name: a.name, price: a.price_label }))}
              />
            </div>
            <div>
              <p className="kicker">Después de publicar tu sitio</p>
              <h2 className="font-display text-[26px] md:text-[30px] mb-5">
                Mantenimiento posterior
              </h2>
              <p className="text-[13px] text-[#666873] mb-5">
                Una vez aprobado y publicado el sitio, los cambios
                posteriores no están incluidos en el precio de desarrollo,
                salvo correcciones derivadas del alcance contratado.
              </p>
              <PriceTable
                testId="table-maintenance"
                rows={data.maintenancePlans.map((m) => ({ name: m.name, price: m.price_label }))}
              />
              <p className="text-[11px] text-[#999aa2] mt-4">{data.maintenanceNotes}</p>
            </div>
          </div>
        </section>

        <section
          data-testid="section-page-types-conditions"
          className="bdk-section"
        >
          <div className="container-bdk max-w-[820px] mx-auto">
            <article style={{ borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)" }}>
              <button
                type="button"
                onClick={(e) =>
                  toggleWithScrollAnchor(e.currentTarget, () =>
                    setConditionsOpen((v) => !v)
                  )
                }
                aria-expanded={conditionsOpen}
                data-testid="conditions-toggle"
                className="w-full flex justify-between items-center gap-5 py-6 text-left cursor-pointer"
              >
                <span className="font-display text-[20px] md:text-[24px] font-black uppercase leading-tight">
                  Condiciones generales
                </span>
                <span
                  className="font-display text-[28px] font-black transition-transform flex-shrink-0"
                  style={{
                    color: "var(--red)",
                    transform: conditionsOpen ? "rotate(45deg)" : "rotate(0)",
                  }}
                >
                  +
                </span>
              </button>
              {conditionsOpen && (
                <div className="pb-8">
                  <ul className="grid gap-2.5 list-none mb-8">
                    {data.generalConditions.map((c, i) => (
                      <li key={i} className="relative pl-5 text-[13px] text-[#666873]">
                        <span
                          className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full"
                          style={{ background: "var(--red)" }}
                        />
                        {c}
                      </li>
                    ))}
                  </ul>
                  <p className="font-display text-[16px] font-black uppercase mb-3">
                    Requieren cotización personalizada
                  </p>
                  <ul className="grid gap-2.5 list-none">
                    {data.customQuoteCases.map((c, i) => (
                      <li key={i} className="relative pl-5 text-[13px] text-[#666873]">
                        <span
                          className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full"
                          style={{ background: "var(--red)" }}
                        />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </article>
          </div>
        </section>

        <section
          data-testid="section-page-types-final-cta"
          className="bdk-section bdk-split-bg--reverse"
        >
          <div className="container-bdk max-w-[700px] mx-auto text-center">
            <h2 className="font-display text-[30px] md:text-[38px]">
              ¿Qué tipo de página necesita tu negocio?
            </h2>
            <p className="mt-4 text-[#666873] text-[14px] md:text-[15px]">
              Indícanos la opción que te interesa y revisamos contigo los
              requerimientos para enviarte una cotización formal.
            </p>
            <a
              href={waUrl(
                "Hola, revisé la lista completa de precios de páginas web de BDKSISTEMAS. Quiero platicar cuál opción necesito."
              )}
              target="_blank"
              rel="noreferrer"
              data-testid="page-types-final-cta"
              onClick={() => window.fbq?.("trackCustom", "ClickCTAListaCompleta")}
              className="bdk-btn bdk-btn--primary mt-6"
            >
              Quiero platicar mi proyecto
            </a>
          </div>
        </section>
      </main>
      <Footer />
      {!NEXO_ENABLED && <WhatsAppFloat />}
      <WhatsAppLeadModal
        open={!!waHref}
        href={waHref}
        onClose={() => setWaHref(null)}
      />
    </div>
  );
};

export default PageTypesPricing;
